import type { LocationQuery } from "vue-router";
import utils from "@/utils/utils";

/** First non-empty query value among the given keys (supports Vue Router array values). */
export function firstQueryValue(q: LocationQuery, ...keys: string[]): string {
  for (const k of keys) {
    const raw = q[k];
    if (raw === undefined || raw === null) continue;
    const s = Array.isArray(raw)
      ? String(raw[raw.length - 1] ?? "")
      : String(raw);
    if (s !== "") return s;
  }
  return "";
}

/** Pinia root slice used when syncing broker URL parameters. */
export type BrokerSyncRootStore = {
  brokerInfoPrefill: string;
  brokerPhone: string;
  brokerName: string;
  brokerCompany: string;
  brokerInfovisible: string;
  brokerEmail: string;
  poolEmail: string;
  poolName: string;
  brokerId: string;
  brokerSubId: string;
  setBrokerPhone: (v: string) => void;
  setBrokerName: (v: string) => void;
  setBrokerCompany: (v: string) => void;
  setBrokerEmail: (v: string) => void;
  setPoolEmail: (v: string) => void;
  setPoolName: (v: string) => void;
  setBrokerId: (v: string) => void;
  setBrokerSubId: (v: string) => void;
  setBrokerInfoPrefill: (v: string) => void;
  setBrokerInfoVisible: (v: string) => void;
};

export function syncBrokerQueryFromRouteOverwrite(
  q: LocationQuery,
  rootStore: BrokerSyncRootStore,
): void {
  const brokerInfoPrefill = firstQueryValue(q, "broker_info_prefill");
  const brokerPhone = firstQueryValue(q, "broker_phone", "vmtel");
  const brokerName = firstQueryValue(q, "broker_name", "vmname");
  const brokerCompany = firstQueryValue(q, "broker_company", "company");
  const brokerInfoVisible = firstQueryValue(q, "broker_info_visible");
  const brokerEmail = firstQueryValue(q, "broker_email", "vmmail");
  const poolEmail = firstQueryValue(q, "pool_email", "poolmail");
  const poolName = firstQueryValue(q, "pool_name", "poolname");
  const brokerId = firstQueryValue(q, "broker_id", "vmnr");
  const brokerSubId = firstQueryValue(q, "broker_sub_id", "vmunr");

  utils.sessionStorageSet("broker_info_prefill", brokerInfoPrefill);
  utils.sessionStorageSet("broker_phone", brokerPhone);
  utils.sessionStorageSet("broker_name", brokerName);
  utils.sessionStorageSet("broker_company", brokerCompany);
  utils.sessionStorageSet("broker_info_visible", brokerInfoVisible);
  utils.sessionStorageSet("broker_email", brokerEmail);
  utils.sessionStorageSet("pool_email", poolEmail);
  utils.sessionStorageSet("pool_name", poolName);
  utils.sessionStorageSet("broker_id", brokerId);
  utils.sessionStorageSet("broker_sub_id", brokerSubId);

  rootStore.setBrokerPhone(brokerPhone);
  rootStore.setBrokerName(brokerName);
  rootStore.setBrokerCompany(brokerCompany);
  rootStore.setBrokerEmail(brokerEmail);
  rootStore.setPoolEmail(poolEmail);
  rootStore.setPoolName(poolName);
  rootStore.setBrokerId(brokerId);
  rootStore.setBrokerSubId(brokerSubId);
  rootStore.setBrokerInfoPrefill(brokerInfoPrefill);
  rootStore.setBrokerInfoVisible(brokerInfoVisible);
}

/** When root fields are still empty, apply query (canonical or short alias) to session + store. */
export function mergeBrokerQueryFromRouteIfStoreEmpty(
  q: LocationQuery,
  rootStore: BrokerSyncRootStore,
): void {
  if (rootStore.brokerInfoPrefill === "") {
    const v = firstQueryValue(q, "broker_info_prefill");
    utils.sessionStorageSet("broker_info_prefill", v);
    rootStore.setBrokerInfoPrefill(v);
  }
  if (rootStore.brokerPhone === "") {
    const v = firstQueryValue(q, "broker_phone", "vmtel");
    utils.sessionStorageSet("broker_phone", v);
    rootStore.setBrokerPhone(v);
  }
  if (rootStore.brokerName === "") {
    const v = firstQueryValue(q, "broker_name", "vmname");
    utils.sessionStorageSet("broker_name", v);
    rootStore.setBrokerName(v);
  }
  if (rootStore.brokerCompany === "") {
    const v = firstQueryValue(q, "broker_company", "company");
    utils.sessionStorageSet("broker_company", v);
    rootStore.setBrokerCompany(v);
  }
  if (rootStore.brokerInfovisible === "") {
    const v = firstQueryValue(q, "broker_info_visible");
    utils.sessionStorageSet("broker_info_visible", v);
    rootStore.setBrokerInfoVisible(v);
  }
  if (rootStore.brokerEmail === "") {
    const v = firstQueryValue(q, "broker_email", "vmmail");
    utils.sessionStorageSet("broker_email", v);
    rootStore.setBrokerEmail(v);
  }
  if (rootStore.poolEmail === "") {
    const v = firstQueryValue(q, "pool_email", "poolmail");
    utils.sessionStorageSet("pool_email", v);
    rootStore.setPoolEmail(v);
  }
  if (rootStore.poolName === "") {
    const v = firstQueryValue(q, "pool_name", "poolname");
    utils.sessionStorageSet("pool_name", v);
    rootStore.setPoolName(v);
  }
  if (rootStore.brokerId === "") {
    const v = firstQueryValue(q, "broker_id", "vmnr");
    utils.sessionStorageSet("broker_id", v);
    rootStore.setBrokerId(v);
  }
  if (rootStore.brokerSubId === "") {
    const v = firstQueryValue(q, "broker_sub_id", "vmunr");
    utils.sessionStorageSet("broker_sub_id", v);
    rootStore.setBrokerSubId(v);
  }
}

/**
 * When the current URL carries broker query values, write them to session + root.
 * Keys omitted from the URL are left unchanged (safe when opening booking without broker params).
 */
export function applyBrokerQueryFromRouteWhenPresent(
  q: LocationQuery,
  rootStore: BrokerSyncRootStore,
): void {
  const apply = (
    value: string,
    sessionKey: string,
    setRoot: (v: string) => void,
  ) => {
    if (value === "") return;
    utils.sessionStorageSet(sessionKey, value);
    setRoot(value);
  };

  apply(firstQueryValue(q, "broker_phone", "vmtel"), "broker_phone", (v) =>
    rootStore.setBrokerPhone(v),
  );
  apply(firstQueryValue(q, "broker_name", "vmname"), "broker_name", (v) =>
    rootStore.setBrokerName(v),
  );
  apply(
    firstQueryValue(q, "broker_company", "company"),
    "broker_company",
    (v) => rootStore.setBrokerCompany(v),
  );
  apply(firstQueryValue(q, "broker_email", "vmmail"), "broker_email", (v) =>
    rootStore.setBrokerEmail(v),
  );
  apply(firstQueryValue(q, "pool_email", "poolmail"), "pool_email", (v) =>
    rootStore.setPoolEmail(v),
  );
  apply(firstQueryValue(q, "pool_name", "poolname"), "pool_name", (v) =>
    rootStore.setPoolName(v),
  );
  apply(firstQueryValue(q, "broker_id", "vmnr"), "broker_id", (v) =>
    rootStore.setBrokerId(v),
  );
  apply(firstQueryValue(q, "broker_sub_id", "vmunr"), "broker_sub_id", (v) =>
    rootStore.setBrokerSubId(v),
  );
  apply(firstQueryValue(q, "broker_info_prefill"), "broker_info_prefill", (v) =>
    rootStore.setBrokerInfoPrefill(v),
  );
  apply(firstQueryValue(q, "broker_info_visible"), "broker_info_visible", (v) =>
    rootStore.setBrokerInfoVisible(v),
  );
}
